import * as React from 'react'
import { createRoot } from 'react-dom/client'
import App from './main/App'
/* global document, Office, module, require */
import { MemoryRouter as Router } from 'react-router-dom'
import * as amplitude from '@amplitude/analytics-browser'
import { ErrorBoundaryWithConfig } from './components/ErrorBoundary'
import { handleAndLogError } from './utils/api-errors'

amplitude.init('95e628cf7c27e0d69136609759238a73', {
    autocapture: true,
})

// Defensive Datadog Logs initialization
if (process.env.NODE_ENV === 'production') {
    try {
        const { datadogLogs } = require('@datadog/browser-logs')
        datadogLogs.init({
            clientToken: 'pub056089f29f55923ba116d4d0b82bbbb3',
            site: 'datadoghq.com',
            service: 'excel',
            env: 'production',
            version: process.env.GIT_COMMIT_HASH,
            // Disabled: We explicitly log errors via datadogLogs.logger.error()
            // forwardErrorsToLogs captures console.error and creates duplicates
            forwardErrorsToLogs: false,
            sessionSampleRate: 100,
        })

        /**
         * Global error handlers for uncaught errors
         * These are safety nets for errors that escape our explicit error handling:
         *
         * References:
         * - MDN window.error: https://developer.mozilla.org/en-US/docs/Web/API/Window/error_event
         * - MDN unhandledrejection: https://developer.mozilla.org/en-US/docs/Web/API/Window/unhandledrejection_event
         * - Datadog logger.error signature: logger.error(message, context, error)
         *   https://docs.datadoghq.com/logs/log_collection/javascript/#error-tracking
         */

        window.addEventListener('error', (event: ErrorEvent) => {
            // Skip errors from cross-origin scripts (they don't have useful info due to CORS)
            // These appear as "Script error." with no stack trace
            if (event.message === 'Script error.' && !event.filename) {
                return
            }

            datadogLogs.logger.error(
                'Uncaught error',
                {
                    errorType: 'uncaught',
                    message: event.message,
                    filename: event.filename,
                    lineno: event.lineno,
                    colno: event.colno,
                },
                event.error instanceof Error ? event.error : undefined
            )
        })
        window.addEventListener(
            'unhandledrejection',
            (event: PromiseRejectionEvent) => {
                const reason = event.reason

                const isError = reason instanceof Error
                const message = isError
                    ? reason.message
                    : typeof reason === 'string'
                    ? reason
                    : 'Unknown rejection reason'

                datadogLogs.logger.error(
                    'Unhandled promise rejection',
                    {
                        errorType: 'unhandledrejection',
                        message,
                        // Include string representation for non-Error rejections
                        reason: isError ? undefined : String(reason),
                    },
                    // Pass Error object for stack trace, undefined otherwise
                    isError ? reason : undefined
                )
            }
        )
    } catch (error) {
        console.warn('Failed to initialize Datadog Logs:', error)
    }
}

const rootContainer = document.getElementById('container')
const root = rootContainer ? createRoot(rootContainer) : null

// Check if we're running in a dialog context (e.g., SSO popup or OAuth callback)
// Dialogs don't have access to RichApi (Excel.run), so we need to skip those calls
// We detect dialog context by:
// 1. URL hash is #sso (initial SSO dialog)
// 2. URL has 'code' query param (OAuth redirect callback in dialog)
const urlParams = new URLSearchParams(window.location.search)
const hasOAuthCode = urlParams.has('code')
const isSsoHash =
    window.location.hash === '#sso' || window.location.hash.startsWith('#sso')
const isDialogContext = isSsoHash || hasOAuthCode

Office.onReady(async (info) => {
    if (info.host === Office.HostType.Excel) {
        // Only run Excel API calls if we're NOT in a dialog context
        // Dialog contexts don't have access to RichApi and will throw errors on Mac
        if (!isDialogContext) {
            // Ensure the shared runtime loads automatically when the document opens
            try {
                if (Office.addin && Office.addin.setStartupBehavior) {
                    await Office.addin.setStartupBehavior(
                        Office.StartupBehavior.load
                    )
                }
            } catch (error) {
                // Failing to set startup behavior shouldn't break the taskpane
                // Use proper error handling to filter permission errors from Datadog
                handleAndLogError(error, {
                    operation: 'setStartupBehavior',
                    context:
                        'Setting add-in startup behavior on Office.onReady',
                })
            }
        }

        // Render the React taskpane UI as before
        root?.render(
            <ErrorBoundaryWithConfig>
                <Router>
                    <App />
                </Router>
            </ErrorBoundaryWithConfig>
        )
    }
})

if ((module as any).hot) {
    ;(module as any).hot.accept('./main/App', () => {
        const NextApp = require('./main/App').default
        root?.render(
            <ErrorBoundaryWithConfig>
                <Router>
                    <NextApp />
                </Router>
            </ErrorBoundaryWithConfig>
        )
    })
}
